import { useEffect, useRef, useState, type CSSProperties } from "react";
import {
  ApiError,
  adminCreateTablet,
  adminIssueTabletToken,
  adminListTablets,
  adminRevokeTabletToken,
  adminUpdateRooms,
  adminUpdateTabletMeta,
} from "../../lib/api";
import type { AdminRoom, AdminTablet } from "../../types/admin";
import { useConfirmDialog } from "../../components/ConfirmDialog";
import OperationsStudioTabs from "../../components/admin/OperationsStudioTabs";

const CONTROL_CHARACTER = /[\u0000-\u001F\u007F]/;

export default function AdminStationTablets() {
  const [tablets, setTablets] = useState<AdminTablet[]>([]);
  const [rooms, setRooms] = useState<AdminRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [savingTabletId, setSavingTabletId] = useState<string | null>(null);
  const [tabletDraft, setTabletDraft] = useState<Record<string, { label: string; room_id: string }>>({});
  const [issuedToken, setIssuedToken] = useState<{
    id: string;
    label: string;
    token: string;
    expires_at: string;
  } | null>(null);
  const [confirmedCopy, setConfirmedCopy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const [newTabletLabel, setNewTabletLabel] = useState("");
  const [newTabletRoomId, setNewTabletRoomId] = useState("");
  const [createAttempt, setCreateAttempt] = useState<{
    request_id: string;
    label: string;
    room_id: string | null;
  } | null>(null);
  const [createConflict, setCreateConflict] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);
  const [creatingTablet, setCreatingTablet] = useState(false);
  const createInFlightRef = useRef(false);
  const { confirm, dialog: confirmDialog } = useConfirmDialog();

  function showFlash(msg: string) {
    setFlash(msg);
    setTimeout(() => setFlash(null), 3000);
  }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await adminListTablets();
      setTablets(r.tablets);
      setRooms(r.rooms ?? []);
      // Keep edits made while a separate tablet operation reloads the list.
      setTabletDraft((previousDrafts) => {
        const drafts: Record<string, { label: string; room_id: string }> = {};
        for (const t of r.tablets) {
          drafts[t.id] = previousDrafts[t.id] ?? { label: t.label, room_id: t.room_id ?? "" };
        }
        return drafts;
      });
    } catch (err: any) {
      setError(err?.message ?? "Could not load tablets");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function createTablet() {
    if (createInFlightRef.current) return;
    setCreateConflict(false);

    const label = newTabletLabel.trim();
    if (!createAttempt && !label) {
      setCreateError("Enter a tablet label.");
      return;
    }
    if (!createAttempt && label.length > 80) {
      setCreateError("Tablet labels must be 80 characters or fewer.");
      return;
    }
    if (!createAttempt && CONTROL_CHARACTER.test(label)) {
      setCreateError("Tablet labels cannot contain control characters.");
      return;
    }

    const payload = createAttempt ?? {
      request_id: crypto.randomUUID(),
      label,
      room_id: newTabletRoomId || null,
    };
    createInFlightRef.current = true;
    setCreatingTablet(true);
    setCreateError(null);
    setCreateSuccess(null);
    setCreateAttempt(payload);
    try {
      const created = await adminCreateTablet(payload);
      setCreateSuccess(`${created.label} was added. Generate device code below when you are ready to pair it.`);
      setNewTabletLabel("");
      setNewTabletRoomId("");
      setCreateAttempt(null);
      await load();
    } catch (err: any) {
      const message = err?.message ?? "Could not add tablet";
      if (err instanceof ApiError && (err.status === 400 || err.status === 404)) {
        // The endpoint has definitively rejected this input before creating an account.
        setCreateAttempt(null);
        setCreateError(message);
      } else if (err instanceof ApiError && err.status === 409) {
        setCreateConflict(true);
        setCreateError(`${message}. Check the tablet list first; the previous attempt may already have created a tablet. Retry to finish adding this tablet.`);
      } else {
        setCreateError(`${message}. Retry to finish adding this tablet.`);
      }
    } finally {
      createInFlightRef.current = false;
      setCreatingTablet(false);
    }
  }

  async function startNewTabletRequest() {
    if (!await confirm({
      title: "Start a new tablet request?",
      message: "Check the tablet list first. The previous attempt may already have created an account. Starting over can add a separate tablet.",
      confirmLabel: "Start over",
    })) return;

    setCreateAttempt(null);
    setCreateConflict(false);
    setCreateError(null);
    setCreateSuccess(null);
    setNewTabletLabel("");
    setNewTabletRoomId("");
  }

  async function saveTablet(t: AdminTablet) {
    const draft = tabletDraft[t.id];
    if (!draft) return;
    const labelChanged = draft.label.trim() !== t.label;
    const roomChanged = (draft.room_id || null) !== (t.room_id || null);
    if (!labelChanged && !roomChanged) {
      showFlash("Nothing changed.");
      return;
    }
    setSavingTabletId(t.id);
    setError(null);
    try {
      await adminUpdateTabletMeta({
        tablet_customer_id: t.id,
        label: draft.label.trim(),
        room_id: draft.room_id || null,
      });
      showFlash(`Saved ${draft.label.trim()}.`);
      // Let the response's normalized row replace this saved draft, while preserving all others.
      setTabletDraft((previousDrafts) => {
        const nextDrafts = { ...previousDrafts };
        delete nextDrafts[t.id];
        return nextDrafts;
      });
      await load();
    } catch (err: any) {
      setError(err?.message ?? "Could not save tablet");
    } finally {
      setSavingTabletId(null);
    }
  }

  async function onIssue(t: AdminTablet) {
    if (!await confirm({
      title: `Generate NEW device code for ${t.label}?`,
      message: "Only 1 active session is permitted per tablet. Generating a new code immediately revokes all prior tokens and terminates any active sessions on other hardware.",
      confirmLabel: "Generate & Revoke Prior",
    })) return;
    setBusyId(t.id);
    setError(null);
    setIssuedToken(null);
    setConfirmedCopy(false);
    try {
      const resp = await adminIssueTabletToken(t.id);
      setIssuedToken({
        id: t.id,
        label: t.label,
        token: resp.token,
        expires_at: resp.expires_at,
      });
      // Refresh the count without showing the token in the list
      load();
    } catch (err: any) {
      setError(err?.message ?? "Could not issue token");
    } finally {
      setBusyId(null);
    }
  }

  async function onRevoke(t: AdminTablet) {
    if (!await confirm({
      title: `Revoke active session for ${t.label}?`,
      message: "Terminates the active session immediately. The tablet will be signed out on its next request.",
      confirmLabel: "Revoke",
      danger: true,
    })) {
      return;
    }
    setBusyId(t.id);
    setError(null);
    try {
      await adminRevokeTabletToken(t.id);
      load();
    } catch (err: any) {
      setError(err?.message ?? "Could not revoke");
    } finally {
      setBusyId(null);
    }
  }

  async function copyToken(token: string) {
    try {
      await navigator.clipboard.writeText(token);
    } catch {
      // Fallback handled by select-on-click in the textarea
    }
  }

  return (
    <section style={styles.shell}>
      <OperationsStudioTabs activeTab="tablets" badgeCounts={{ tablets: tablets.length }} />
      <header>
        <p style={styles.eyebrow}>Owner only</p>
        <h1 style={styles.title}>Station Tablets</h1>
        <p style={styles.body}>
          Each tablet operates under a strict <strong>single active session policy</strong> (1 active device at a time).
          Generating a new device code automatically revokes all prior tokens and terminates active sessions across all devices.
          Paste into the tablet immediately at <code>/set-station-token</code>.
        </p>
      </header>

      {flash && <div style={styles.flash} role="status">{flash}</div>}
      {error && <div style={styles.error}>{error}</div>}

      <RoomsCard
        rooms={rooms}
        onSaved={(newRooms) => {
          setRooms(newRooms);
          showFlash("Rooms saved.");
          load();
        }}
        onError={(msg) => setError(msg)}
      />

      <section style={styles.createTabletCard} aria-labelledby="add-tablet-heading">
        <h2 id="add-tablet-heading" style={styles.cardTitle}>Add tablet</h2>
        <p style={styles.cardSubtitle}>
          Create a tablet entry here, then use Generate Code below to sign in the new device.
        </p>
        <form
          style={styles.createTabletForm}
          onSubmit={(event) => {
            event.preventDefault();
            void createTablet();
          }}
        >
          <label style={styles.createTabletField}>
            <span style={styles.fieldLabel}>Tablet label</span>
            <input
              type="text"
              value={createAttempt?.label ?? newTabletLabel}
              onChange={(event) => {
                if (CONTROL_CHARACTER.test(event.target.value)) {
                  setCreateError("Tablet labels cannot contain control characters.");
                  return;
                }
                setCreateError(null);
                setNewTabletLabel(event.target.value);
              }}
              placeholder="e.g. Service Bar"
              maxLength={80}
              required
              disabled={creatingTablet || Boolean(createAttempt)}
              style={styles.input}
            />
          </label>
          <label style={styles.createTabletField}>
            <span style={styles.fieldLabel}>Room <span style={styles.optional}>Optional</span></span>
            <select
              value={createAttempt?.room_id ?? newTabletRoomId}
              onChange={(event) => setNewTabletRoomId(event.target.value)}
              disabled={creatingTablet || Boolean(createAttempt)}
              style={styles.input}
            >
              <option value="">— Unassigned —</option>
              {rooms.map((room) => <option key={room.id} value={room.id}>{room.name}</option>)}
            </select>
          </label>
          <button type="submit" disabled={creatingTablet} style={styles.addTabletBtn}>
            {creatingTablet ? "Adding tablet…" : createAttempt ? "Retry adding tablet" : "Add tablet"}
          </button>
          {createConflict && (
            <button type="button" onClick={() => void startNewTabletRequest()} style={styles.startOverBtn}>
              Start over
            </button>
          )}
        </form>
        {createError && <p style={styles.createError} role="alert">{createError}</p>}
        {createSuccess && <p style={styles.createSuccess} role="status">{createSuccess}</p>}
      </section>


      {issuedToken && (
        <div style={styles.tokenReveal}>
          <p style={styles.tokenLabel}>
            New token for <strong>{issuedToken.label}</strong> · expires{" "}
            {new Date(issuedToken.expires_at).toLocaleDateString()}
          </p>
          <textarea
            value={issuedToken.token}
            readOnly
            onClick={(e) => (e.target as HTMLTextAreaElement).select()}
            style={styles.tokenBox}
          />
          <div style={styles.tokenActions}>
            <button
              type="button"
              onClick={() => void copyToken(issuedToken.token)}
              style={styles.copyBtn}
            >
              Copy to clipboard
            </button>
            <label style={styles.confirmLabel}>
              <input
                type="checkbox"
                checked={confirmedCopy}
                onChange={(e) => setConfirmedCopy(e.target.checked)}
              />
              I copied it (this token cannot be retrieved again)
            </label>
            <button
              type="button"
              onClick={() => {
                setIssuedToken(null);
                setConfirmedCopy(false);
              }}
              disabled={!confirmedCopy}
              style={{
                ...styles.dismissBtn,
                opacity: confirmedCopy ? 1 : 0.4,
                cursor: confirmedCopy ? "pointer" : "not-allowed",
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p style={styles.muted}>Loading tablets…</p>
      ) : (
        <ul style={styles.list}>
          {tablets.map((t) => {
            const expiresMs = t.latest_expires_at
              ? new Date(t.latest_expires_at).getTime()
              : 0;
            const daysLeft = expiresMs
              ? Math.round((expiresMs - Date.now()) / (24 * 60 * 60 * 1000))
              : 0;
            const busy = busyId === t.id;
            return (
              <li key={t.id} style={styles.tabletRow}>
                <div style={styles.tabletEdit}>
                  <input
                    type="text"
                    value={tabletDraft[t.id]?.label ?? ""}
                    onChange={(e) =>
                      setTabletDraft((prev) => ({
                        ...prev,
                        [t.id]: { ...(prev[t.id] ?? { label: "", room_id: "" }), label: e.target.value },
                      }))
                    }
                    aria-label={`Tablet label for ${t.email}`}
                    placeholder="e.g. Library Bar"
                    style={{ ...styles.input, ...styles.tabletEditLabel }}
                    disabled={savingTabletId === t.id}
                  />
                  <select
                    value={tabletDraft[t.id]?.room_id ?? ""}
                    onChange={(e) =>
                      setTabletDraft((prev) => ({
                        ...prev,
                        [t.id]: { ...(prev[t.id] ?? { label: "", room_id: "" }), room_id: e.target.value },
                      }))
                    }
                    aria-label={`Room for ${t.label}`}
                    style={{ ...styles.input, ...styles.tabletEditRoom }}
                    disabled={savingTabletId === t.id}
                  >
                    <option value="">— Unassigned —</option>
                    {rooms.map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => void saveTablet(t)}
                    disabled={savingTabletId === t.id}
                    style={{ ...styles.saveTabletBtn, ...styles.tabletEditSave }}
                  >
                    {savingTabletId === t.id ? "…" : "Save"}
                  </button>
                </div>
                <div style={styles.tabletEmailRow}>
                  <span style={styles.rowSub}>{t.email}</span>
                  {t.room_name && <span style={styles.roomChip}>{t.room_name}</span>}
                </div>
                <div style={styles.tabletSessionRow}>
                  <div style={styles.rowMeta}>
                    <strong style={{ color: t.active_session_count > 0 ? "var(--gold)" : "var(--muted)" }}>
                      {t.active_session_count > 0 ? "● 1 Active Device" : "○ Inactive / Unpaired"}
                    </strong>
                    {t.latest_expires_at && t.active_session_count > 0 && (
                      <>
                        {" · expires "}
                        {new Date(t.latest_expires_at).toLocaleDateString()}{" "}
                        <span style={{ color: daysLeft < 30 ? "#FF8A7A" : "var(--muted)" }}>
                          ({daysLeft}d)
                        </span>
                      </>
                    )}
                  </div>
                  <div style={styles.rowActions}>
                    <button
                      type="button"
                      onClick={() => void onIssue(t)}
                      disabled={busy}
                      style={styles.issueBtn}
                      title="Generate a new device code and revoke all prior sessions"
                    >
                      {busy ? "…" : "Generate Code"}
                    </button>
                    <button
                      type="button"
                      onClick={() => void onRevoke(t)}
                      disabled={busy || t.active_session_count === 0}
                      style={{
                        ...styles.revokeBtn,
                        opacity: t.active_session_count === 0 ? 0.4 : 1,
                      }}
                    >
                      Revoke all
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {confirmDialog}
    </section>
  );
}

interface RoomsCardProps {
  rooms: AdminRoom[];
  onSaved: (rooms: AdminRoom[]) => void;
  onError: (msg: string) => void;
}

function RoomsCard({ rooms, onSaved, onError }: RoomsCardProps) {
  const [draft, setDraft] = useState<Array<{ id?: string; name: string; sort_order: number }>>(
    () => rooms.map((r, i) => ({ id: r.id, name: r.name, sort_order: r.sort_order ?? i + 1 })),
  );
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  // Re-seed when the parent's rooms list changes (e.g. after a successful save).
  useEffect(() => {
    setDraft(rooms.map((r, i) => ({ id: r.id, name: r.name, sort_order: r.sort_order ?? i + 1 })));
  }, [rooms]);

  function addRoom() {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setDraft([...draft, { name: trimmed, sort_order: draft.length + 1 }]);
    setNewName("");
  }
  function removeRoom(idx: number) {
    setDraft(draft.filter((_, i) => i !== idx));
  }
  function renameRoom(idx: number, name: string) {
    setDraft(draft.map((r, i) => (i === idx ? { ...r, name } : r)));
  }

  async function save() {
    setBusy(true);
    try {
      const payload = draft
        .map((r, i) => ({ id: r.id, name: r.name.trim(), sort_order: i + 1 }))
        .filter((r) => r.name);
      const res = await adminUpdateRooms(payload);
      onSaved(res.rooms);
    } catch (err: any) {
      onError(err?.message ?? "Could not save rooms");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={styles.roomsCard} role="group" aria-labelledby="rooms-heading">
      <h2 id="rooms-heading" style={styles.cardTitle}>Rooms</h2>
      <p style={styles.cardSubtitle}>
        Physical rooms in the venue. Tablets are assigned to a room so future
        multi-tablet awareness can show what siblings in the same room are
        currently making. Removing a room sets all tablets in it to unassigned.
      </p>

      {draft.length === 0 && <p style={styles.muted}>No rooms yet. Add one below.</p>}

      <ol style={styles.roomList}>
        {draft.map((r, idx) => (
          <li key={r.id ?? `new-${idx}`} style={styles.roomRow}>
            <span style={styles.roomIdx}>{idx + 1}.</span>
            <input
              type="text"
              value={r.name}
              onChange={(e) => renameRoom(idx, e.target.value)}
              aria-label={`Room ${idx + 1} name`}
              style={styles.input}
              disabled={busy}
            />
            <button
              type="button"
              onClick={() => removeRoom(idx)}
              aria-label={`Remove room ${r.name}`}
              style={styles.roomRemoveBtn}
              disabled={busy}
            >
              ✕
            </button>
          </li>
        ))}
      </ol>

      <div style={styles.roomAddRow}>
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addRoom();
            }
          }}
          placeholder="New room name"
          aria-label="New room name"
          style={styles.input}
          disabled={busy}
        />
        <button type="button" onClick={addRoom} style={styles.addRoomBtn} disabled={busy}>
          Add room
        </button>
      </div>

      <div style={styles.roomSaveRow}>
        <button type="button" onClick={save} disabled={busy} style={styles.saveRoomsBtn}>
          {busy ? "Saving…" : "Save rooms"}
        </button>
      </div>
    </section>
  );
}

const styles: Record<string, CSSProperties> = {
  shell: {
    // 95% of available width with a sane cap on big screens; centered.
    // Keeps the rooms + tablet rows usable on phones (≥320px) up through
    // desktop (1100px effective max).
    width: "95%",
    maxWidth: 1100,
    margin: "0 auto",
  },
  flash: {
    margin: "0 0 12px",
    padding: 10,
    background: "rgba(39,174,96,0.12)",
    border: "1px solid rgba(39,174,96,0.4)",
    color: "#86efac",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 700,
  },
  // Rooms card
  roomsCard: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  createTabletCard: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  createTabletForm: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "end",
    gap: 10,
  },
  createTabletField: {
    display: "grid",
    gap: 5,
    flex: "1 1 190px",
    minWidth: 0,
  },
  fieldLabel: { color: "var(--text)", fontSize: 12, fontWeight: 700 },
  optional: { color: "var(--muted)", fontWeight: 500 },
  addTabletBtn: {
    background: "var(--gold)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 10,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 40,
    flex: "0 0 auto",
  },
  startOverBtn: {
    background: "transparent",
    color: "var(--muted)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    minHeight: 40,
    flex: "0 0 auto",
  },
  createError: { color: "#FF8A7A", fontSize: 12, fontWeight: 700, margin: "10px 0 0" },
  createSuccess: { color: "#86efac", fontSize: 12, fontWeight: 700, margin: "10px 0 0" },
  cardTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 20,
    margin: "0 0 4px",
    color: "var(--text)",
  },
  cardSubtitle: { color: "var(--muted)", fontSize: 12, margin: "0 0 8px", lineHeight: 1.4 },
  roomList: { listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 6 },
  roomRow: {
    display: "grid",
    gridTemplateColumns: "32px minmax(0, 1fr) 40px",
    gap: 8,
    alignItems: "center",
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: 8,
  },
  roomIdx: {
    fontSize: 11,
    color: "var(--muted)",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    textAlign: "center",
  },
  roomRemoveBtn: {
    width: 40,
    height: 40,
    background: "transparent",
    border: "1px solid rgba(224,80,64,0.4)",
    borderRadius: 8,
    color: "#FF8A7A",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  },
  roomAddRow: { display: "flex", gap: 8, marginTop: 4 },
  addRoomBtn: {
    background: "var(--bg)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 40,
    whiteSpace: "nowrap",
  },
  roomSaveRow: { display: "flex", justifyContent: "flex-end", marginTop: 4 },
  saveRoomsBtn: {
    background: "var(--gold)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 10,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 40,
  },
  // Tablet rows (extended)
  tabletRow: {
    display: "grid",
    gap: 8,
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    padding: 12,
  },
  tabletEdit: {
    // flex-wrap so label / room / Save stack vertically on narrow screens
    // (each child below has min-width that forces wrap < ~360px).
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    alignItems: "center",
  },
  tabletEditLabel: {
    flex: "1 1 160px",
    minWidth: 0,
  },
  tabletEditRoom: {
    flex: "1 1 140px",
    minWidth: 0,
  },
  tabletEditSave: {
    flex: "0 0 auto",
  },
  tabletEmailRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  tabletSessionRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    paddingTop: 6,
    borderTop: "1px solid var(--border)",
  },
  saveTabletBtn: {
    background: "var(--gold)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 10,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 40,
  },
  roomChip: {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "var(--gold)",
    background: "rgba(249,175,54,0.12)",
    border: "1px solid rgba(249,175,54,0.3)",
    borderRadius: 999,
    padding: "2px 8px",
  },
  input: {
    height: 40,
    background: "var(--bg)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: "0 10px",
    fontSize: 13,
    fontFamily: "inherit",
  },
  eyebrow: {
    margin: 0,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--gold)",
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 28,
    margin: "8px 0 8px",
    letterSpacing: -0.4,
  },
  body: {
    color: "var(--muted)",
    fontSize: 13,
    lineHeight: 1.5,
    margin: "0 0 16px",
  },
  error: {
    background: "rgba(224,80,64,0.1)",
    border: "1px solid rgba(224,80,64,0.4)",
    color: "#FF8A7A",
    padding: 10,
    borderRadius: 10,
    fontSize: 13,
    marginBottom: 12,
  },
  tokenReveal: {
    background: "rgba(249,175,54,0.06)",
    border: "1px solid var(--gold)",
    borderRadius: 12,
    padding: 14,
    marginBottom: 16,
  },
  tokenLabel: {
    margin: "0 0 8px",
    fontSize: 13,
    color: "var(--text)",
    fontWeight: 700,
  },
  tokenBox: {
    width: "100%",
    minHeight: 60,
    boxSizing: "border-box",
    background: "var(--bg)",
    color: "var(--gold)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 10,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 12,
    resize: "vertical",
  },
  tokenActions: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    alignItems: "center",
    marginTop: 10,
  },
  copyBtn: {
    background: "var(--gold)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 10,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
  confirmLabel: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    color: "var(--text)",
    fontWeight: 600,
  },
  dismissBtn: {
    background: "transparent",
    color: "var(--muted)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 700,
    marginLeft: "auto",
  },
  list: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "grid",
    gap: 8,
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr auto",
    gap: 12,
    alignItems: "center",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    padding: 12,
  },
  rowName: { fontSize: 14, fontWeight: 800, lineHeight: 1.2 },
  rowSub: {
    fontSize: 11,
    color: "var(--muted)",
    fontWeight: 600,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    marginTop: 2,
  },
  rowMeta: { fontSize: 12, color: "var(--muted)", lineHeight: 1.4 },
  metaLine: {},
  rowActions: { display: "flex", gap: 6 },
  issueBtn: {
    background: "var(--gold)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 10,
    padding: "8px 12px",
    fontSize: 11,
    fontWeight: 800,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  revokeBtn: {
    background: "rgba(224,80,64,0.16)",
    color: "#FF8A7A",
    border: "1px solid rgba(224,80,64,0.4)",
    borderRadius: 10,
    padding: "8px 12px",
    fontSize: 11,
    fontWeight: 800,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  muted: { color: "var(--muted)", fontSize: 13 },
};
